/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param */
import { Logger } from '@/utils/logger';
import type { PortalNamespace } from '@montugroup/circuit-shared';
import {
  findRoutePortalOwner,
  removeSearchParam,
  useAuthFromMagicLink,
  useMagicLinkAuthNavigation,
  useSession,
} from '@montugroup/circuit-shared';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { viteDomainSplitBaseUrl } from '@montugroup/circuit-shared/constants/env';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import { useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const logger = new Logger('usePortalRedirector');

let authTokenAttempts = 0;

/**
 * Handles redirections between circuit portals & transfering authentication state across domains
 */
export function usePortalRedirector() {
  const { session } = useSession();
  const { data: user } = useUser();
  const { getAuthFromMagicLink, isPending: isPendingAuthFromMagicLink } = useAuthFromMagicLink();
  const { magicLinkAuthNavigation, isPending: isPendingMagicLinkAuthNavigation } = useMagicLinkAuthNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isPendingRedirection = isPendingAuthFromMagicLink || isPendingMagicLinkAuthNavigation;

  const removeTokenFromUrl = useCallback(() => {
    searchParams.delete('token');
    logger.info('removeTokenFromUrl', { searchParams: searchParams.toString(), authTokenAttempts });
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const navigateToRemoveTokenFromUrl = useCallback(() => {
    const urlWithoutToken = removeSearchParam({ url: window.location.href, key: 'token' });
    removeTokenFromUrl();
    logger.info('navigateToRemoveTokenFromUrl', { urlWithoutToken, authTokenAttempts });
    window.location.replace(urlWithoutToken);
  }, [removeTokenFromUrl]);

  // TODO: Remove overly cautious error handling and excessive logging if no issues are found
  useLayoutEffect(() => {
    if (isPendingRedirection) {
      return;
    }

    const token = searchParams.get('token') as string;
    if (typeof token === 'string') {
      try {
        (async () => {
          logger.info('hasTokenSearchParam', {
            tokenLength: token.length,
            tokenCharacters: [...new Set(token.split('').sort())].join(''),
          });

          const auth = await getAuthFromMagicLink({ token, apiClient: getTsApiV1Client() });
          logger.info('getAuthFromMagicLink', { hasAuth: !!auth });

          AuthService.storeAuthStateFromLogin(auth);
          logger.info('AuthService.storeAuthStateFromLogin');
          removeTokenFromUrl();
        })();
      } catch (error) {
        logger.error('hasTokenSearchParam::error', { error });
        removeTokenFromUrl();
      } finally {
        authTokenAttempts++;
        logger.info(`getAuthFromMagicLink::hasAttemptedTokenRemoval::${authTokenAttempts}`, { authTokenAttempts });

        if (authTokenAttempts >= 3) {
          // If something is preventing a graceful removal of the token from the URL, try a hard navigation
          navigateToRemoveTokenFromUrl();
        }
      }
      return;
    }

    // This logic is used when we know which portal we want to redirect to at the call site,
    // this requires we pass the a PortalNamespace on location.state.destinationPortal on navigation
    try {
      const destinationPortal = location?.state?.destinationPortal as PortalNamespace;
      const isEscalatedAuthNavigation = !!destinationPortal;
      if (isEscalatedAuthNavigation) {
        const currentURL = window.location.href;
        const newOrigin = viteDomainSplitBaseUrl(destinationPortal);

        logger.info('isEscalatedAuthNavigation', {
          destinationPortal,
          currentURL,
          newOrigin,
        });
        magicLinkAuthNavigation({
          currentURL,
          newOrigin,
          apiClient: getTsApiV1Client(),
          authorization: session?.token,
        });
        return;
      }
    } catch (error) {
      logger.error('isEscalatedAuthNavigation::error', { error });
    }

    // The following check is used to ensure we redirect to another portal if the route is "owned" by that portal
    // this will only be reached if it's accessed via external means eg an external link
    try {
      const newAppNamespace = findRoutePortalOwner({
        currentAppNamespace: 'patient',
        currentPath: location.pathname,
      });
      if (newAppNamespace) {
        const currentURL = window.location.href;
        const newOrigin = viteDomainSplitBaseUrl(newAppNamespace);

        logger.info('hasNewAppNamespace', {
          newAppNamespace,
          currentURL,
          newOrigin,
        });
        magicLinkAuthNavigation({
          currentURL,
          newOrigin,
          apiClient: getTsApiV1Client(),
          authorization: session?.token,
        });
        return;
      }
    } catch (error) {
      logger.error('hasNewAppNamespace::error', { error });
    }
  }, [
    getAuthFromMagicLink,
    isPendingRedirection,
    location.pathname,
    location?.state?.destinationPortal,
    magicLinkAuthNavigation,
    navigate,
    navigateToRemoveTokenFromUrl,
    removeTokenFromUrl,
    searchParams,
    session?.token,
    user,
  ]);

  return {
    isPendingRedirection,
  };
}
